import { useState, useEffect, Fragment } from 'react'
import WebsiteScraperTool from '../components/WebsiteScraperTool';
import { validateYoutubeUrl } from '../utils/youtube';
import { dispatchCreditUpdateEvent, startUseCredit } from '../utils/creditSystem';
import { sendToWebhook, youtbebuToText } from '../utils/api';
import { saveToHistory } from '../utils/storage';
import Toast from '../components/Toast';
import { convertPdfToExcel, validatePdfFile } from '../utils/fileUtils';
import FileUploader from '../components/pdf/FileUploader';
import FilePreview from '../components/pdf/FilePreview';
import Instructions from '../components/pdf/Instructions';
import PdfToXls from '../components/PdfToXls';

function Workflow() {
    const [selectedTool, setSelectedTool] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    try {

        useEffect(() => {
            // Check if user is authenticated
            const token = localStorage.getItem('token');
            if (!token) {
                window.location.href = '/auth';
                return;
            }
            setIsAuthenticated(true);
        }, []);

        const tools = [
            {
                id: 'youtube-to-text',
                name: 'River YouTube 轉文字',
                description: '將 YouTube 影片轉換成文字內容',
                icon: 'fa-brands fa-youtube',
                color: 'bg-red-500',
                component: YouTubeToText
            },
            {
                id: 'website-scraper',
                name: 'River 網站內容擷取',
                description: '擷取網站的文字內容並進行處理',
                icon: 'fa-solid fa-globe',
                color: 'bg-indigo-500',
                component: WebsiteScraperTool
            },
            {
                id: 'pdf-to-excel',
                name: 'River PDF 轉 Excel',
                description: '將 PDF 檔案轉換成 Excel 表格',
                icon: 'fa-solid fa-file-excel',
                color: 'bg-green-500',
                component: PdfToXls
            },
            // {
            //     id: 'image-generator',
            //     name: 'WOLO 圖片生成器',
            //     description: '使用 AI 生成各種風格的圖片',
            //     icon: 'fa-solid fa-image',
            //     color: 'bg-purple-500',
            //     component: ComingSoon
            // },
            // {
            //     id: 'text-summarizer',
            //     name: 'WOLO 文字摘要',
            //     description: '自動生成長文章的摘要內容',
            //     icon: 'fa-solid fa-align-left',
            //     color: 'bg-blue-500',
            //     component: ComingSoon
            // }
        ];

        const handleSelectTool = (toolId) => {
            setSelectedTool(toolId);
        };

        const handleBack = () => {
            setSelectedTool(null);
        };

        if (!isAuthenticated) {
            return null; // Will redirect in useEffect
        }

        if (selectedTool) {
            const tool = tools.find(t => t.id === selectedTool);
            if (!tool) return null;
            
            const ToolComponent = tool.component;
            
            return (
                <div className="container mx-auto px-4 py-8" data-name="workflow-tool-page" style={{"paddingTop":"32px","paddingBottom":"32px","paddingLeft":"16px","paddingRight":"16px","marginTop":"0px","marginBottom":"0px","marginLeft":"163px","marginRight":"163px","fontSize":"16px","color":"rgb(0, 0, 0)","backgroundColor":"rgba(0, 0, 0, 0)"}}>
                    <button 
                        onClick={handleBack}
                        className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
                        data-name="back-button"
                    >
                        <i className="fas fa-arrow-left mr-2"></i>
                        返回工作流列表
                    </button>
                    
                    <ToolComponent />
                </div>
            );
        }

        return (
            <div className="container mx-auto px-4 py-12" data-name="workflow-page">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-3xl font-bold mb-4" data-name="page-title">River AI</h1>
                        <p className="text-gray-600" data-name="page-description">
                            選擇需要使用的工具，提升工作效率
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6" data-name="tools-grid">
                        {tools.map(tool => (
                            <div 
                                key={tool.id}
                                className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all cursor-pointer"
                                onClick={() => handleSelectTool(tool.id)}
                                data-name={`tool-card-${tool.id}`}
                            >
                                <div className="p-6">
                                    <div className="flex items-center mb-4">
                                        <div className={`w-12 h-12 ${tool.color} rounded-lg flex items-center justify-center text-white`}>
                                            <i className={`${tool.icon} text-xl`}></i>
                                        </div>
                                        <h3 className="text-xl font-semibold ml-4">{tool.name}</h3>
                                    </div>
                                    <p className="text-gray-600">{tool.description}</p>
                                    <div className="mt-4 flex justify-end">
                                        <button className="text-blue-600 hover:text-blue-800 flex items-center">
                                            開始使用
                                            <i className="fas fa-chevron-right ml-1"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('Workflow page error:', error);
        reportError(error);
        return null;
    }
}

// YouTube to Text component
function YouTubeToText() {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [content, setContent] = useState('');
    const [status, setStatus] = useState('idle'); // idle, converting, completed, error
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    try {

        const handleCopy = async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                setToast({
                    show: true,
                    message: '已複製到剪貼簿',
                    type: 'success'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            } catch (err) {
                console.error('Copy failed:', err);
                setToast({
                    show: true,
                    message: '複製失敗',
                    type: 'error'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setContent('');
            setStatus('idle');

            if (!url.trim()) {
                setError('請輸入 YouTube 網址');
                return;
            }

            if (!validateYoutubeUrl(url)) {
                setError('請輸入有效的 YouTube 網址');
                return;
            }

            setIsLoading(true);
            setStatus('converting');

            try {
                // Then send to webhook for conversion
                const response = await youtbebuToText(url);
                if (!response.success) {
                    throw new Error(response.error);
                }

                dispatchCreditUpdateEvent(response.data.credit);
                setContent(response.data.data);
                setStatus('completed');

                setUrl(''); // Clear input after successful submission
            } catch (err) {
                setError(err.message);
                setStatus('error');
                console.error('Submission error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        const renderStatus = () => {
            switch (status) {
                case 'converting':
                    return (
                        <div 
                            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
                            data-name="converting-message"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-blue-800">
                                    正在處理中...
                                </h3>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                            <p className="text-blue-600">
                                正在轉換 YouTube 內容，請稍候...
                            </p>
                        </div>
                    );
                case 'completed':
                    return (
                        <div 
                            className="bg-green-50 border border-green-200 rounded-lg p-6"
                            data-name="result-container"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-green-800">
                                    轉換完成！
                                </h3>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleCopy(content)}
                                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                        data-name="copy-button"
                                    >
                                        <i className="fas fa-copy mr-2"></i>
                                        複製文字
                                    </button>
                                    <a
                                        href="/history"
                                        className="inline-flex items-center px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                                        data-name="history-link"
                                    >
                                        <i className="fas fa-history mr-2"></i>
                                        查看歷史記錄
                                    </a>
                                </div>
                            </div>
                            <div className="mt-4 bg-white rounded-lg p-4 border border-green-100">
                                <h4 className="font-medium text-green-800 mb-2">內容文字：</h4>
                                <p className="text-gray-700 whitespace-pre-wrap">
                                    {content}
                                </p>
                            </div>
                        </div>
                    );
                default:
                    return null;
            }
        };

        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-name="workflow-container">
                    <div className="px-8 py-6 bg-gradient-to-r from-red-600 to-red-400">
                        <h1 className="text-2xl font-bold text-white" data-name="page-title">
                            River YouTube 轉文字
                        </h1>
                        <p className="text-white/80 mt-2">
                            將 YouTube 影片轉換成文字內容
                        </p>
                    </div>

                    <div className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                                    YouTube 網址
                                </label>
                                <div className="flex gap-4">
                                    <input
                                        type="url"
                                        id="url"
                                        value={url}
                                        onChange={(e) => {
                                            setUrl(e.target.value);
                                            setError('');
                                            setStatus('idle');
                                        }}
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-all"
                                        disabled={isLoading}
                                        data-name="url-input"
                                    />
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-200 transition-all flex items-center justify-center min-w-[120px]"
                                        disabled={isLoading}
                                        data-name="submit-button"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        ) : (
                                            <Fragment>
                                                <i className="fas fa-sync mr-2"></i>
                                                轉換
                                            </Fragment>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div 
                                    className="bg-red-50 border-l-4 border-red-400 p-4 rounded"
                                    role="alert"
                                    data-name="error-message"
                                >
                                    <div className="flex">
                                        <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                        <p className="text-red-700">{error}</p>
                                    </div>
                                </div>
                            )}

                            {renderStatus()}

                            <div className="mt-8 border-t pt-8" data-name="instructions">
                                <h2 className="text-xl font-semibold mb-4">使用說明</h2>
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                            <i className="fas fa-link text-red-600"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-medium mb-1">貼上網址</h3>
                                            <p className="text-sm text-gray-600">複製 YouTube 影片網址並貼上</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                            <i className="fas fa-sync text-red-600"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-medium mb-1">轉換</h3>
                                            <p className="text-sm text-gray-600">等待系統轉換成文字</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                            <i className="fas fa-copy text-red-600"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-medium mb-1">複製內容</h3>
                                            <p className="text-sm text-gray-600">複製轉換後的文字內容</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('YouTubeToText component error:', error);
        reportError(error);
        return null;
    }
}

// PDF to Excel component
function _PdfToXls() {
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    try {

        const handleFileSelect = (selectedFile) => {
            setError('');
            setSuccess(false);
            setDownloadUrl(null);
            setDownloadFileName('');

            const validation = validatePdfFile(selectedFile);
            if (!validation.isValid) {
                setError(validation.error);
                return;
            }

            setFile(selectedFile);
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setSuccess(false);
            setDownloadUrl(null);
            setDownloadFileName('');

            if (!file) {
                setError('請選擇檔案');
                return;
            }

            setIsLoading(true);

            try {
                const result = await convertPdfToExcel(file);
                
                if (result.success) {
                    setSuccess(true);
                    
                    if (result.data.fileBuffer) {
                        // Create a blob from the array buffer
                        try {
                            const blob = new Blob([result.data.fileBuffer], { 
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                            });
                            
                            // Create a download URL
                            const url = window.URL.createObjectURL(blob);
                            setDownloadUrl(url);
                            
                            // Set the download filename
                            const fileName = file.name.replace('.pdf', '.xlsx');
                            setDownloadFileName(fileName);
                        } catch (err) {
                            console.error("Error creating blob:", err);
                            setError('轉換檔案格式時發生錯誤');
                        }
                    }
                    
                    setFile(null);
                    
                    // Reset file input
                    const fileInput = document.getElementById('pdf-file');
                    if (fileInput) {
                        fileInput.value = '';
                    }
                } else {
                    setError(result.error);
                }
            } catch (err) {
                setError('轉換過程中發生錯誤，請稍後再試');
                console.error('Conversion error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        const handleDownload = () => {
            if (downloadUrl && downloadFileName) {
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = downloadFileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                
                setToast({
                    show: true,
                    message: '檔案下載中...',
                    type: 'success'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            }
        };

        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-name="converter-container">
                    <div className="px-8 py-6 bg-gradient-to-r from-green-600 to-green-400">
                        <h1 className="text-2xl font-bold text-white" data-name="page-title">
                            River PDF 轉 Excel
                        </h1>
                        <p className="text-white/80 mt-2">
                            將 PDF 檔案轉換成 Excel 表格
                        </p>
                    </div>

                    <div className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-6" data-name="converter-form">
                            <FileUploader 
                                onFileSelect={handleFileSelect}
                                isLoading={isLoading}
                            />

                            {file && (
                                <FilePreview 
                                    file={file}
                                    onRemove={() => setFile(null)}
                                />
                            )}

                            {error && (
                                <div 
                                    className="bg-red-50 border-l-4 border-red-400 p-4 rounded"
                                    role="alert"
                                    data-name="error-message"
                                >
                                    <div className="flex">
                                        <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                        <p className="text-red-700">{error}</p>
                                    </div>
                                </div>
                            )}

                            {success && (
                                <div 
                                    className="bg-green-50 border-l-4 border-green-400 p-4 rounded"
                                    role="alert"
                                    data-name="success-message"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <i className="fas fa-check-circle text-green-400 mr-3"></i>
                                            <p className="text-green-700">檔案轉換成功！</p>
                                        </div>
                                        {downloadUrl && (
                                            <button
                                                type="button"
                                                onClick={handleDownload}
                                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center"
                                                data-name="download-button"
                                            >
                                                <i className="fas fa-download mr-2"></i>
                                                下載 Excel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all flex items-center justify-center"
                                disabled={!file || isLoading}
                                data-name="submit-button"
                            >
                                {isLoading ? (
                                    <Fragment>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                        轉換中...
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <i className="fas fa-sync mr-2"></i>
                                        開始轉換
                                    </Fragment>
                                )}
                            </button>
                        </form>

                        <Instructions />
                    </div>
                </div>
                
                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('PdfToXls component error:', error);
        reportError(error);
        return null;
    }
}

// Coming Soon component for unimplemented tools
function ComingSoon() {
    try {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden text-center py-16 px-4" data-name="coming-soon">
                    <div className="mb-6">
                        <i className="fas fa-tools text-6xl text-gray-300"></i>
                    </div>
                    <h2 className="text-3xl font-bold mb-4">功能開發中</h2>
                    <p className="text-gray-600 max-w-md mx-auto mb-8">
                        我們正在努力開發這項功能，敬請期待！
                    </p>
                    <div className="inline-flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg">
                        <i className="fas fa-clock mr-2"></i>
                        即將推出
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('ComingSoon component error:', error);
        reportError(error);
        return null;
    }
}

export default Workflow;